import * as THREE from 'three';
import type {PointSnapRequest, PointSnapResult} from './PointSnapTypes';
import type {PointSnapStrategyType} from './PointSnapStrategyType';
import type {Viewer} from '../../viewer';
import {SketchPointObject} from '../../mesh/sketch/SketchPointObject';
import type {AbstractSketchEdgeObject} from '../../mesh/sketch/AbstractSketchEdgeObject';
import {
    projectWorldToScreen as utilProjectWorldToScreen,
    pointToSegmentDistance as utilPointToSegmentDistance,
    computeEdgeScreenDistance as utilComputeEdgeScreenDistance,
} from '../utils/ScreenSpaceUtils';

/**
 * 点捕捉策略抽象父类。
 * 职责：
 * 1. 定义点捕捉策略统一类型标识；
 * 2. 定义点捕捉策略统一启用状态；
 * 3. 持有 Viewer 引用，提供屏幕坐标投影工具；
 * 4. 定义点捕捉策略统一求解接口；
 * 5. 约束所有具体捕捉子类的最小行为边界。
 */
export abstract class AbstractPointSnapStrategy {
    /** 策略类型。 */
    public abstract readonly strategyType: PointSnapStrategyType;

    /** 是否启用。 */
    public enabled: boolean = true;

    /** Viewer 实例，由 PointSnapManager 在注册后统一注入。 */
    protected viewer: Viewer | null = null;

    /**
     * 捕捉判定像素阈值（屏幕像素距离）。
     * 说明：
     * 1. 候选点投影到屏幕后，若与鼠标位置的像素距离超过此值，则不触发捕捉；
     * 2. 值越大，捕捉范围越宽松，建议保持在 10~20px 范围内。
     */
    protected readonly snapPixelThreshold: number = 10;

    /**
     * 边缘悬停像素阈值。
     * 说明：鼠标到边缘几何的屏幕像素距离小于此值时，认为悬停在该边缘上。
     */
    protected readonly edgeHoverPixelThreshold: number = 15;

    /** 点捕捉辅助线统一显示长度。 */
    protected readonly pointSnapGuideLineLength: number = 100;

    /**
     * 设置 Viewer 实例。
     * @param viewer Viewer 实例。
     */
    public setViewer(viewer: Viewer): void {
        this.viewer = viewer;
    }

    /**
     * 设置策略启用状态。
     * @param enabled 是否启用。
     */
    public setEnabled(enabled: boolean): void {
        this.enabled = enabled;
    }

    /**
     * 清除策略管理的预览场景对象（如引导线）。
     * 说明：在绘制结束或工具切换时由 PointSnapManager 广播调用。
     * 需要管理场景对象的子类应重写此方法并在其中清理 THREE.js 对象。
     */
    public clearPreview(): void {}

    /**
     * 获取当前场景对象（通过 Viewer 访问）。
     * @returns 场景对象；viewer 未就绪时返回 null。
     */
    protected get scene(): THREE.Scene | null {
        return this.viewer?.scene ?? null;
    }

    /**
     * 通过 Viewer 统一入口挂载预览对象。
     *
     * 设计说明：
     * 1. 点捕捉策略创建的引导线和预览点同样属于场景运行时对象；
     * 2. 如果这里直接调用 `scene.add()`，会绕过 BVH 统一接入链路；
     * 3. 在抽象基类集中收口后，所有子策略都自动继承同一套入场约束。
     *
     * @param object 待挂载对象。
     * @param parent 可选父节点；缺省时挂到主场景。
     * @returns 已挂载对象。
     */
    protected addPreviewObject<TObject extends THREE.Object3D>(
        object: TObject,
        parent?: THREE.Object3D,
    ): TObject {
        if (!this.viewer) {
            throw new Error('点捕捉策略挂载预览对象失败：Viewer 未注入。');
        }

        return this.viewer.addObject(object, parent ?? this.viewer.scene) as TObject;
    }

    /**
     * 将世界坐标投影到屏幕像素坐标。
     * 说明：
     * 1. 使用 Viewer 的相机和渲染器完成投影；
     * 2. 点在相机背后（NDC z > 1）时返回 null；
     * 3. viewer 未就绪时也返回 null。
     * @param position 世界坐标。
     * @returns 屏幕像素坐标（原点在左上角）；不可投影时返回 null。
     */
    protected projectWorldToScreen(position: THREE.Vector3): THREE.Vector2 | null {
        if (!this.viewer) {
            return null;
        }
        return utilProjectWorldToScreen(this.viewer, position);
    }

    /**
     * 获取当前活跃草图组中维护的所有草图点对象集合。
     * 说明：
     * 1. 直接读取 SketchGroup 实时维护的 _sketchPointObjectSet，无需遍历场景；
     * 2. viewer 或 sketchGroupManager 或 activeSketchGroup 不存在时返回 null。
     * @returns 草图点对象只读集合；不可用时返回 null。
     */
    protected getActiveSketchPointObjects(): ReadonlySet<SketchPointObject> | null {
        return this.viewer?.sketchGroupManager?.activeSketchGroup?.getSketchPointObjects() ?? null;
    }

    /**
     * 获取当前活跃草图组中的端点集合。
     * @returns 端点只读集合；不可用时返回 null。
     */
    protected getActiveEndPoints(): ReadonlySet<SketchPointObject> | null {
        return this.viewer?.sketchGroupManager?.activeSketchGroup?.getEndPoints() ?? null;
    }

    /**
     * 获取当前活跃草图组中的中点集合。
     * @returns 中点只读集合；不可用时返回 null。
     */
    protected getActiveMidPoints(): ReadonlySet<SketchPointObject> | null {
        return this.viewer?.sketchGroupManager?.activeSketchGroup?.getMidPoints() ?? null;
    }

    /**
     * 获取当前活跃草图组中的圆心点集合。
     * @returns 圆心点只读集合；不可用时返回 null。
     */
    protected getActiveCenterPoints(): ReadonlySet<SketchPointObject> | null {
        return this.viewer?.sketchGroupManager?.activeSketchGroup?.getCenterPoints() ?? null;
    }

    /**
     * 获取当前活跃草图组中的象限点集合。
     * @returns 象限点只读集合；不可用时返回 null。
     */
    protected getActiveQuadrantPoints(): ReadonlySet<SketchPointObject> | null {
        return this.viewer?.sketchGroupManager?.activeSketchGroup?.getQuadrantPoints() ?? null;
    }

    /**
     * 获取当前活跃草图组中的用户草图点集合（SketchPointKind.SketchPoint）。
     * @returns 用户草图点只读集合；不可用时返回 null。
     */
    protected getActiveSketchKindPoints(): ReadonlySet<SketchPointObject> | null {
        return this.viewer?.sketchGroupManager?.activeSketchGroup?.getSketchKindPoints() ?? null;
    }

    
    /**
     * 判断对象是否被当前捕捉请求排除。
     *
     * @param request 点捕捉请求。
     * @param targetObject 待判断对象。
     * @returns 对象在排除集合中返回 true。
     *
     * 设计说明：
     * 1. Move / Copy 预览阶段要继续捕捉其它对象上的点；
     * 2. 但当前正在变换的对象及其控制点不能参与捕捉，否则鼠标会吸附回自身；
     * 3. 该判断集中在抽象策略中，避免各策略重复解释排除集合语义。
     */
    protected isSnapTargetExcluded(
        request: PointSnapRequest,
        targetObject: THREE.Object3D | null | undefined,
    ): boolean {
        if (!targetObject || !request.excludedTargetObjectIds) {
            return false;
        }
        return request.excludedTargetObjectIds.has(targetObject.uuid);
    }
    

    
    /**
     * 计算受统一长度限制的辅助线终点。
     *
     * 设计说明：
     * 1. 该方法只影响点捕捉预览辅助线的显示长度，不修改真实捕捉结果；
     * 2. 起点到终点短于统一长度时保留原终点，避免近距离辅助线反向延长造成误导；
     * 3. 起点到终点超过统一长度时，沿原方向截断到 100，保持不同策略的辅助线视觉长度一致。
     *
     * @param start 辅助线显示起点。
     * @param end 原始辅助线显示终点。
     * @returns 已按统一长度限制后的终点。
     */
    protected resolveLimitedPointSnapGuideLineEndPoint(
        start: THREE.Vector3,
        end: THREE.Vector3,
    ): THREE.Vector3 {
        const direction = new THREE.Vector3().subVectors(end, start);
        const length = direction.length();
        if (length <= this.pointSnapGuideLineLength || length <= 1e-9) {
            return end.clone();
        }

        return start.clone().addScaledVector(
            direction.divideScalar(length),
            this.pointSnapGuideLineLength,
        );
    }
    

    
    /**
     * 从统一 hover 链路中解析当前活跃草图内的边对象。
     *
     * 设计说明：
     * 1. 选择层已经负责屏幕空间精细拾取与候选过滤；
     * 2. 这里仅做类型、预览对象、排除集合和活跃草图归属校验；
     * 3. 具体策略通过类型守卫描述自己支持的源几何，避免重复遍历草图边。
     *
     * @param request 点捕捉请求。
     * @param isSupportedTarget 源对象类型守卫。
     * @param isPreviewTarget 判断源对象是否为绘制预览对象。
     * @returns 活跃草图中的合法悬停源对象；无合法源对象时返回 null。
     */
    protected resolveHoveredActiveSketchEdge<TSupportedTarget extends AbstractSketchEdgeObject>(
        request: PointSnapRequest,
        isSupportedTarget: (object: THREE.Object3D) => object is TSupportedTarget,
        isPreviewTarget: (target: TSupportedTarget) => boolean,
    ): TSupportedTarget | null {
        const hoveredObject = request.hoveredObject ?? null;
        if (!hoveredObject) {
            return null;
        }

        // 悬停对象必须是当前策略支持的草图边类型，否则不能作为方向源几何。
        if (!isSupportedTarget(hoveredObject)) {
            return null;
        }

        // 绘制预览对象只承担视觉反馈职责，不能被关系型捕捉当作正式参考。
        if (isPreviewTarget(hoveredObject)) {
            return null;
        }

        // Move / Copy 等命令会显式排除正在变换的对象，避免吸附回自身。
        if (this.isSnapTargetExcluded(request, hoveredObject)) {
            return null;
        }

        const edges = this.viewer?.sketchGroupManager?.activeSketchGroup?.getSketchEdgeObjects();
        if (!edges) {
            return null;
        }

        // 最后校验归属，防止其它场景对象或非活跃草图边进入当前草图捕捉链路。
        return edges.has(hoveredObject) ? hoveredObject : null;
    }
    

    /**
     * 执行点捕捉求解。
     * @param request 点捕捉请求参数。
     * @returns 点捕捉结果。
     */
    public abstract resolve(request: PointSnapRequest): PointSnapResult | null;

    /**
     * 计算鼠标到边缘对象的屏幕像素距离。
     * 说明：
     * 1. 从边缘的 BufferGeometry 中提取所有顶点；
     * 2. 将每对相邻顶点投影到屏幕空间；
     * 3. 计算鼠标点到每条屏幕线段的距离，取最小值。
     * @param edge 边缘对象。
     * @param screenPos 鼠标屏幕坐标。
     * @returns 最短像素距离；无法计算时返回 null。
     */
    protected computeScreenDistanceToEdge(
        edge: AbstractSketchEdgeObject,
        screenPos: THREE.Vector2,
    ): number | null {
        if (!this.viewer) {
            return null;
        }
        return utilComputeEdgeScreenDistance(this.viewer, edge.geometry, screenPos);
    }

    /**
     * 计算二维点到线段的最短距离。
     * 说明：使用向量投影公式，将点投影到线段所在直线，再夹紧到线段范围内。
     * @param p 待测点。
     * @param a 线段起点。
     * @param b 线段终点。
     * @returns 点到线段的距离。
     */
    protected static pointToSegmentDistance(
        p: THREE.Vector2,
        a: THREE.Vector2,
        b: THREE.Vector2,
    ): number {
        return utilPointToSegmentDistance(p, a, b);
    }

    /**
     * 计算鼠标屏幕点到“过起点、沿给定方向的投影线段”的屏幕像素距离。
     * 说明：
     * 1. 先将 rawPoint 投影到过 firstPoint 的目标方向线上；
     * 2. 再把起点和投影点投影到屏幕空间，计算鼠标到该屏幕线段的距离；
     * 3. 返回投影点与屏幕距离，供平行/垂直/相切等方向型捕捉统一复用。
     * @param firstPoint 绘制起点。
     * @param rawPoint 当前鼠标世界坐标。
     * @param direction 目标方向（归一化世界坐标）。
     * @param screenPosition 当前鼠标屏幕坐标。
     * @returns 投影点与屏幕距离；无法计算时返回 null。
     */
    protected resolveProjectedLineScreenDistance(
        firstPoint: THREE.Vector3,
        rawPoint: THREE.Vector3,
        direction: THREE.Vector3,
        screenPosition: THREE.Vector2,
    ): { projectedWorld: THREE.Vector3; screenDistance: number } | null {
        if (direction.lengthSq() < 1e-18) {
            return null;
        }

        const toMouse = new THREE.Vector3().subVectors(rawPoint, firstPoint);
        const projectionLength = toMouse.dot(direction);
        const projectedWorld = firstPoint.clone().addScaledVector(direction, projectionLength);

        const screenStart = this.projectWorldToScreen(firstPoint);
        const screenEnd = this.projectWorldToScreen(projectedWorld);
        if (!screenStart || !screenEnd) {
            return null;
        }

        return {
            projectedWorld,
            screenDistance: AbstractPointSnapStrategy.pointToSegmentDistance(
                screenPosition,
                screenStart,
                screenEnd,
            ),
        };
    }
}
